import { RegistrationResult, RegistrationUserData, SagaGenerator } from '../../types';
import * as E from 'fp-ts/Either';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';
import {
    RegistrationConfirmation,
    RegistrationContext,
    registrationV2Slice,
    TwikeyResponse,
    TwikeyResponseStatus,
    TwikeyValidationError
} from './registrationV2Slice';
import {
    createCompanyTask,
    createTeamleaderTask,
    createUserTask,
    createVehiclesTask,
    getCompanyTask,
    loginUserTask,
    RegistrationError,
    requestTeamleaderSubscriptionTask,
    requestTwikeyFormUrlTask,
    syncUserAsHubspotCustomerTask
} from "./registrationV2ApiAdaptor";
import { pipe } from "fp-ts/function";
import * as TE from 'fp-ts/TaskEither';
import { Either, match } from 'fp-ts/Either';
import { createUserError, userSlice } from "../user/userSlice";
import { authSlice } from "../auth/authSlice";
import * as companyActions from "../company/companyActions";
import { v4 as uuidv4 } from "uuid";
import { callApi } from '../../root/appSaga';
import { Api } from '../../api';
import { checkExitUrlSignature, getWebsiteKey } from './twikeyUtils';
import * as Sentry from '@sentry/react';
import { setSentryScope } from 'sentryTools';
import { RegistrationFlowType } from 'myParkdUtils/enums';
import { GAAction, trackDataLayerEvent } from 'myParkdUtils/googleAnalytics';


export const MAX_NUMBER_OF_CARS_REQUIRING_MANDATE = 50;

export const isTwikeyMandateRequired = (flowType: RegistrationFlowType, numberOfCars: number): boolean =>
    (flowType === 'TRACK_AND_TRACE' && numberOfCars <= MAX_NUMBER_OF_CARS_REQUIRING_MANDATE) || (flowType === 'OEM')

const shouldProcessTwikeyFlow = (flowType: RegistrationFlowType, numberOfCars: number, mandateAcceptedByUser?: boolean): boolean =>
    isTwikeyMandateRequired(flowType, numberOfCars) || (mandateAcceptedByUser ?? false)

export function* registrationV2Saga(): SagaGenerator {
    return yield all([
        takeEvery(registrationV2Slice.actions.registerV2, registerUser),
        takeEvery(registrationV2Slice.actions.processTwikeyResponse, processTwikeyResult),
        takeEvery(registrationV2Slice.actions.triggerConfirmRegistration, confirmRegistration),
        takeEvery(registrationV2Slice.actions.registerV2Completed, trackRegistrationConversion)
    ]);
}

export function* registerUser(params: ReturnType<typeof registrationV2Slice.actions.registerV2>) {

    const {
        registrationCompany,
        user,
        vehicles,
        flowType,
        mandateAcceptedByUser,
        numberOfCars,
        paymentType,
        telematicProviderType,
        otherTelematicProvider,
        sector } = params.payload;

    const initialRegistrationContext: RegistrationContext = {
        step: 0,
        registrationId: uuidv4(),
        flowType,
        numberOfVehicles: numberOfCars,
        twikeyPaymentType: paymentType,
        telematicProviderType,
        otherTelematicProvider,
        sector
    }

    const billingRegistrationFlow = () =>
        TE.chain((registrationContext: RegistrationContext) =>
            pipe(createTeamleaderTask(registrationContext),
                TE.chain((registrationContext) => {
                    return shouldProcessTwikeyFlow(flowType, numberOfCars, mandateAcceptedByUser) ?
                        pipe(requestTwikeyFormUrlTask(registrationContext),
                            TE.chain((registrationContext) =>
                                TE.right(registrationContext)
                            )
                        )
                        :
                        TE.right(registrationContext);
                }
                )
            )
        );

    const userCompanyTask: TE.TaskEither<RegistrationError, RegistrationContext> = pipe(
        createUserTask(user, initialRegistrationContext),
        TE.chain((registrationContext) =>
            pipe(loginUserTask(registrationContext),
                TE.chain((registrationContext) =>
                    pipe(createCompanyTask(registrationCompany, registrationContext),
                        TE.chain((registrationContext) =>
                            pipe(getCompanyTask(registrationContext),
                                TE.chain((registrationContext) =>
                                    pipe(syncUserAsHubspotCustomerTask(registrationContext),
                                        TE.chain((registrationContext) =>
                                            pipe(createVehiclesTask(vehicles ?? [], registrationContext),
                                                billingRegistrationFlow()
                                            )
                                        )
                                    )
                                )
                            )
                        )
                    )
                )
            )
        )
    );

    const resultUserCompanyCreation: Either<RegistrationError, RegistrationContext> = yield call(userCompanyTask);

    // @ts-ignore
    yield* pipe(
        resultUserCompanyCreation,
        match(
            (error: RegistrationError) => function* () {
                if (error.errorType === 'UserRegistrationError') {
                    yield put(createUserError({ error: error.details }))
                } else if (error.errorType === 'UserLoginError') {
                    yield put(authSlice.actions.loginError({ error: error.details }))
                } else if (error.errorType === 'CompanyCreationError') {
                    yield put(companyActions.createCompanyError(error.details))
                } else if (error.errorType === 'CompanyDetailsError') {
                    yield put(companyActions.getCompanyDetailsError(error.details))
                }
                yield put(registrationV2Slice.actions.registerV2Error({ error }))

            }, // On Left (error)
            // @ts-ignore
            (registrationContext: RegistrationContext) => function* () {
                yield put(userSlice.actions.createUserCompleted({ user: registrationContext.user as unknown as RegistrationUserData }));
                yield put(authSlice.actions.loginCompleted({ user: registrationContext.user!, token: registrationContext.token! }));
                yield put(companyActions.createCompanyCompleted(registrationCompany))
                yield put(companyActions.getCompanyDetailsCompleted(registrationContext.company!))
                setSentryScope(registrationContext.token!, registrationContext.user!, registrationContext.company!);
                yield put(registrationV2Slice.actions.registerV2Completed({ registrationContext }))

                if (!shouldProcessTwikeyFlow(flowType, numberOfCars, mandateAcceptedByUser)) {
                    yield put(registrationV2Slice.actions.triggerConfirmRegistration({ registrationContext }));

                }
            } // On Right (success)
        )
    )();
}

type TwikeyValidationResult = { isValid: boolean, status: TwikeyResponseStatus, validationError?: TwikeyValidationError };

export function* processTwikeyResult(params: ReturnType<typeof registrationV2Slice.actions.processTwikeyResponse>) {
    const twikeyValidation: TwikeyValidationResult = yield call(validateTwikeyResponse, params.payload);

    if (twikeyValidation.isValid) {
        if (params.payload.registrationId) {
            const registrationContext: RegistrationContext = yield select(registrationV2Slice.selectors.getRegistrationContext);

            const subscriptionTask: TE.TaskEither<RegistrationError, RegistrationContext> = yield call(requestTeamleaderSubscriptionTask, registrationContext)
            const result: Either<RegistrationError, RegistrationContext> = yield call(subscriptionTask);

            if (E.isRight(result)) {
                yield put(registrationV2Slice.actions.teamleaderSubscriptionCompleted({ registrationContext: result.right }))
                yield put(registrationV2Slice.actions.triggerConfirmRegistration({}));
            } else {
                yield put(registrationV2Slice.actions.teamleaderSubscriptionError({ error: result.left }))
            }
        }
        yield put(registrationV2Slice.actions.onSuccessfulTwikeyResponse(twikeyValidation.status));
    } else {
        yield put(registrationV2Slice.actions.onErrorOnTwikeyResponse({ error: twikeyValidation.validationError! }))
    }
}

const validateTwikeyResponse = async (twikeyResponse: TwikeyResponse): Promise<TwikeyValidationResult> => {
    const signatureValidation = await validateTwikeySignature(twikeyResponse.signature,
        twikeyResponse.mandateNumber,
        twikeyResponse.status,
        twikeyResponse.registrationId);

    if (!signatureValidation.isValid) {
        return { isValid: false, status: twikeyResponse.status, validationError: { errorType: 'SignatureIncorrect', details: signatureValidation.error } }
    } else {
        return { isValid: true, status: twikeyResponse.status }
    }
};

const validateTwikeySignature = async (signature: string,
    mandate: string,
    status: TwikeyResponseStatus,
    registrationId?: string): Promise<{ isValid: boolean, error?: string }> => {
    const websiteKey = getWebsiteKey();

    if (await checkExitUrlSignature(websiteKey, mandate, status, signature, registrationId)) {
        return { isValid: true };
    } else {
        return { isValid: false, error: 'Twikey response signature is not valid' }
    }
}

export function* confirmRegistration(params: ReturnType<typeof registrationV2Slice.actions.triggerConfirmRegistration>) {
    try {
        let registrationContext: RegistrationContext | undefined = params.payload.registrationContext;
        if (registrationContext === undefined) {
            registrationContext = yield select(registrationV2Slice.selectors.getRegistrationContext)
        }

        const registrationConfirmation: RegistrationConfirmation = {
            registrationType: registrationContext?.flowType!,
            numberOfCars: registrationContext?.numberOfVehicles!,
            telematicProvider: registrationContext?.telematicProviderType
        }

        const registrationResult: RegistrationResult = yield callApi(Api.confirmRegistration, registrationConfirmation);
        if (registrationResult?.companyStatus === 'ONBOARDED') {
            yield put(companyActions.getCompanyDetails()); // triggers navigation (onboarding -> vehicles)
        }

    } catch (err) {
        console.error(err);
        Sentry.captureException(err);
    }
}

export function* trackRegistrationConversion(params: ReturnType<typeof registrationV2Slice.actions.registerV2Completed>) {
    yield call(trackDataLayerEvent, {
        event: GAAction.REGISTRATION_ACCOUNT_CREATION_COMPLETED,
        fleetSize: params?.payload?.registrationContext?.numberOfVehicles
    })
}
